/* eslint-disable prefer-const -- Date cursor objects are mutated without reassignment. */
"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import "./improvements.css";
import { AuthForms } from "./AuthForms";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import {
  PERSON_NAMES,
  choiceExtrasForLevel,
  createPeopleRound,
  createUniqueChunkNumber,
  createWordRound,
  orderDueCards,
} from "./gameContent";
import type { PersonCue } from "./gameContent";
import {
  advanceLevel,
  difficultyFor,
  freshLevel,
  normalizeLevelState,
  normalizeLevels,
} from "./gameProgress";
import type { LevelState, ModeId } from "./gameProgress";
import {
  currentPushSubscription,
  disableDevicePush,
  enableDevicePush,
  pushSupport,
  saveDevicePush,
} from "./push";
import type { PushSupport } from "./push";

type View = "today" | "train" | "progress" | "learn";
type Mode = {
  id: ModeId;
  icon: string;
  title: string;
  note: string;
  meta: string;
  tone: string;
  science: string;
  how: string;
  example: string;
  mentor: string;
};
type Result = {
  id: string;
  mode: string;
  score: number;
  correct: number;
  total: number;
  duration_seconds: number;
  played_at: string;
  level: number;
  effective_level: number;
  retention_check: boolean;
};
type DeckCard = {
  id: string;
  prompt: string;
  answer: string;
  intervalDays: number;
  dueAt: string;
};
const dayKey = (value: string | Date) => {
  const d = new Date(value);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
const MODES: Mode[] = [
  {
    id: "palace",
    icon: "⌁",
    title: "Memory Palace",
    note: "Place vivid ideas along a familiar route",
    meta: "2–4 min · Guided",
    tone: "mint",
    science: "Spatial cues give each item a distinctive retrieval path.",
    how: "Choose a route you know well. Put one exaggerated image at each stop, then mentally walk the route.",
    example: "Milk explodes on your front door; bread dances on the sofa.",
    mentor: "Reuse the same route until its stops feel automatic.",
  },
  {
    id: "link",
    icon: "◇",
    title: "Link Lab",
    note: "Build and recall one strange story",
    meta: "2–4 min · Creative",
    tone: "lilac",
    science: "Elaboration and imagery create more ways back to the idea.",
    how: "Connect each item to the next with one vivid, moving, slightly absurd story. Write a short version before recall begins.",
    example: "An otter plays a violin that fires pepper into a comet.",
    mentor: "Motion and surprise are usually stickier than a sensible story.",
  },
  {
    id: "recall",
    icon: "✦",
    title: "Recall Rush",
    note: "Retrieve before the answer appears",
    meta: "1–2 min · Fast",
    tone: "peach",
    science:
      "Retrieval practice strengthens later access better than restudying alone.",
    how: "Hide the answer and genuinely try to retrieve it. Check only after making an attempt.",
    example: "Before rereading a chapter, write three ideas you remember.",
    mentor:
      "A hard but successful retrieval is useful; guessing endlessly is not.",
  },
  {
    id: "names",
    icon: "◎",
    title: "Name Forge",
    note: "Meet rotating people and bind names to visible cues",
    meta: "2–4 min · Social",
    tone: "sky",
    science:
      "Focused attention plus a meaningful association supports name recall.",
    how: "Each round introduces fresh people. Repeat each name, notice the highlighted stable feature, and create a respectful visual association.",
    example: "Rose has rosy glasses; picture a tiny rose resting on them.",
    mentor: "Use the person’s name naturally once during the conversation.",
  },
  {
    id: "numbers",
    icon: "#",
    title: "Number Shapes",
    note: "Choose chunks for one long number",
    meta: "2–3 min · Pattern",
    tone: "yellow",
    science:
      "Chunking reduces the number of separate units held in working memory.",
    how: "Choose how to split one long number, then attach rhythm, dates, or familiar patterns to your chunks.",
    example: "19452024 becomes 1945 · 2024 instead of eight separate digits.",
    mentor: "Keep chunks between two and four digits when possible.",
  },
  {
    id: "spaced",
    icon: "↻",
    title: "Spaced Deck",
    note: "Review persistent cards when they are due",
    meta: "2–5 min · Review",
    tone: "rose",
    science: "Spacing and successful retrieval improve long-term retention.",
    how: "Study a new card once, then answer later due cards without seeing the answer first. Correct cards return after a longer gap; missed cards return sooner.",
    example:
      "A correct card returns tomorrow, then after three days, then one week.",
    mentor: "Put a question on the front, not a vague topic label.",
  },
];
const WORDS = [
  "lantern",
  "otter",
  "velvet",
  "comet",
  "cinnamon",
  "bridge",
  "violin",
  "pepper",
  "anchor",
  "orchid",
  "castle",
  "lemon",
  "telescope",
  "badger",
  "ribbon",
  "volcano",
  "ginger",
  "balcony",
  "trumpet",
  "marble",
  "compass",
  "tulip",
  "lighthouse",
  "coconut",
  "backpack",
  "penguin",
  "feather",
  "thunder",
  "saffron",
  "staircase",
  "piano",
  "magnet",
  "suitcase",
  "willow",
  "waterfall",
  "pumpkin",
  "helmet",
  "dolphin",
  "blanket",
  "rocket",
  "teapot",
  "peacock",
  "diamond",
  "carousel",
  "avocado",
  "windmill",
  "typewriter",
  "jellyfish",
  "snowflake",
  "basket",
  "dragon",
  "umbrella",
  "camera",
  "strawberry",
  "saxophone",
  "hourglass",
  "parrot",
  "mountain",
  "keyhole",
  "sunflower",
];
const APP_BUILD = "2026.08.18.6";
const DAILY_ORDER = [
  MODES[2],
  MODES[0],
  MODES[1],
  MODES[3],
  MODES[4],
  MODES[5],
];
const PALACE_LOCI = [
  "Front door",
  "Entry mat",
  "Coat hook",
  "Sofa",
  "Lamp",
  "Kitchen sink",
  "Fridge",
  "Bed",
  "Mirror",
];
const NAMES = [
  "Maya",
  "Theo",
  "Nora",
  "Felix",
  "Amina",
  "Jonah",
  "Clara",
  "Mateo",
  "Iris",
  "Hugo",
  "Leila",
  "Owen",
  "Sofia",
  "Eli",
  "Zara",
  "Miles",
];
const STARTER_DECK = [
  {
    id: "retrieval",
    prompt: "What strengthens access more than simply rereading?",
    answer: "retrieval practice",
  },
  {
    id: "spacing",
    prompt: "What practice schedule returns after some forgetting?",
    answer: "spaced practice",
  },
  {
    id: "chunk",
    prompt: "What technique groups digits into fewer units?",
    answer: "chunking",
  },
  {
    id: "loci",
    prompt: "What technique places ideas along a familiar route?",
    answer: "memory palace",
  },
  {
    id: "story",
    prompt: "What makes a linking story more memorable?",
    answer: "vivid motion",
  },
  {
    id: "attempt",
    prompt: "What should happen before you reveal an answer?",
    answer: "a genuine retrieval attempt",
  },
  {
    id: "miss",
    prompt: "How should a missed card be scheduled?",
    answer: "return it sooner",
  },
  {
    id: "route",
    prompt: "What kind of route works best for a memory palace?",
    answer: "a familiar route",
  },
  {
    id: "name-cue",
    prompt: "What makes a useful cue for remembering a name?",
    answer: "a stable noticeable feature",
  },
  {
    id: "distinctive",
    prompt: "What usually makes an association easier to retrieve?",
    answer: "distinctive vivid detail",
  },
  {
    id: "desirable",
    prompt: "Why can some retrieval difficulty be useful?",
    answer: "it strengthens later access",
  },
  {
    id: "feedback",
    prompt: "When is corrective feedback most useful?",
    answer: "after an honest attempt",
  },
];
const storageKey = (
  userId: string | null,
  kind: "results" | "levels" | "deck" | "workout",
) => `memsoup:v2:${userId ?? "guest"}:${kind}`;
const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
};
const initialDeck = (): DeckCard[] =>
  STARTER_DECK.map((card) => ({
    ...card,
    intervalDays: 0,
    dueAt: new Date(0).toISOString(),
  }));
const mergeStarterDeck = (cards: DeckCard[]) => {
  const existing = new Set(cards.map((card) => card.id));
  return [
    ...cards,
    ...initialDeck().filter((card) => !existing.has(card.id)),
  ];
};
function shuffled<T>(items: T[]) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}
function buildNumberDistractors(sequence: string[], count: number) {
  const extras: string[] = [];
  for (let attempt = 0; extras.length < count && attempt < 100; attempt++) {
    const source = sequence[attempt % sequence.length] ?? "0";
    const modulus = 10 ** source.length;
    const offset = 1 + ((attempt * 7 + 3) % Math.max(1, modulus - 1));
    const candidate = String((Number(source) + offset) % modulus).padStart(
      source.length,
      "0",
    );
    if (!sequence.includes(candidate) && !extras.includes(candidate))
      extras.push(candidate);
  }
  return extras;
}

function buildChoices(
  mode: Mode,
  sequence: string[],
  level: number,
  spacedAnswers: string[] = [],
) {
  let extras: string[];
  const extraCount = choiceExtrasForLevel(level);
  if (mode.id === "numbers")
    extras = buildNumberDistractors(sequence, Math.min(3, extraCount));
  else if (mode.id === "names")
    extras = shuffled(
      [...new Set([...NAMES, ...PERSON_NAMES])].filter(
        (value) => !sequence.includes(value),
      ),
    ).slice(0, extraCount);
  else if (mode.id === "spaced")
    extras = shuffled(
      [...STARTER_DECK.map((card) => card.answer), ...spacedAnswers].filter(
        (value) => !sequence.includes(value),
      ),
    ).slice(0, extraCount);
  else
    extras = shuffled(WORDS.filter((value) => !sequence.includes(value))).slice(
      0,
      extraCount,
    );
  return shuffled([...new Set([...sequence, ...extras])]);
}

function storyCoverage(story: string, sequence: string[]) {
  const normalized = story.toLocaleLowerCase();
  return sequence.filter((word) => normalized.includes(word.toLocaleLowerCase()))
    .length;
}

function tone(ok: boolean) {
  try {
    const C =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const c = new C(),
      o = c.createOscillator(),
      g = c.createGain();
    o.frequency.value = ok ? 620 : 190;
    g.gain.setValueAtTime(0.06, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.18);
    o.connect(g).connect(c.destination);
    o.start();
    o.stop(c.currentTime + 0.18);
  } catch {
    return;
  }
}

function SequenceCards({
  game,
  sequence,
  people,
  deck,
}: {
  game: Mode;
  sequence: string[];
  people: PersonCue[];
  deck: DeckCard[];
}) {
  if (game.id === "numbers")
    return (
      <div className="number-study" aria-label="Number to chunk">
        {sequence.join("")}
      </div>
    );
  if (game.id === "names")
    return (
      <div className="people-grid">
        {people.map((person) => (
          <article key={person.id}>
            <span aria-hidden="true">{person.avatar}</span>
            <strong>{person.name}</strong>
            <small>{person.feature}</small>
          </article>
        ))}
      </div>
    );
  if (game.id === "spaced")
    return (
      <div className="deck-study">
        {deck.map((card) => (
          <article key={card.id}>
            <b>Question</b>
            <span>{card.prompt}</span>
            <b>Answer</b>
            <strong>{card.answer}</strong>
          </article>
        ))}
      </div>
    );
  return (
    <div
      className={`sequence ${game.id === "palace" ? "palace-sequence" : ""}`}
    >
      {sequence.map((word, index) => (
        <span key={`${word}-${index}`}>
          <b>{game.id === "palace" ? PALACE_LOCI[index] : index + 1}</b>
          {word}
        </span>
      ))}
    </div>
  );
}

export default function Home() {
  const [view, setView] = useState<View>("today"),
    [goal, setGoal] = useState(15),
    [reminderEnabled, setReminderEnabled] = useState(false),
    [reminderTime, setReminderTime] = useState("19:00"),
    [pushState, setPushState] = useState<
      "unknown" | "ready" | "busy" | "enabled" | "denied" | PushSupport
    >("unknown"),
    [user, setUser] = useState<User | null>(null),
    [auth, setAuth] = useState(false),
    [message, setMessage] = useState(""),
    [authReady, setAuthReady] = useState(false),
    [recovering, setRecovering] = useState(false),
    [info, setInfo] = useState<Mode | null>(null);
  const [game, setGame] = useState<Mode | null>(null),
    [phase, setPhase] = useState<"study" | "recall" | "done" | "caughtup">(
      "study",
    ),
    [sequence, setSequence] = useState<string[]>([]),
    [choices, setChoices] = useState<string[]>([]),
    [picked, setPicked] = useState<string[]>([]),
    [results, setResults] = useState<Result[]>([]),
    [sound, setSound] = useState(true),
    [started, setStarted] = useState(0),
    [levels, setLevels] = useState<Record<string, LevelState>>({}),
    [roundLevel, setRoundLevel] = useState(1),
    [retentionCheck, setRetentionCheck] = useState(false),
    [levelChange, setLevelChange] = useState(0),
    [masteryGained, setMasteryGained] = useState(false),
    [workoutIndex, setWorkoutIndex] = useState<number | null>(null),
    [story, setStory] = useState(""),
    [chunkSize, setChunkSize] = useState(0),
    [people, setPeople] = useState<PersonCue[]>([]),
    [deck, setDeck] = useState<DeckCard[]>([]),
    [roundDeck, setRoundDeck] = useState<DeckCard[]>([]),
    [studyingNewCards, setStudyingNewCards] = useState(false),
    [hydratedOwner, setHydratedOwner] = useState("pending"),
    [newPrompt, setNewPrompt] = useState(""),
    [newAnswer, setNewAnswer] = useState(""),
    [resumeIndex, setResumeIndex] = useState<number | null>(null);
  const recallTimer = useRef<ReturnType<typeof setTimeout> | null>(null),
    round = useRef(0),
    pickedNow = useRef<string[]>([]),
    choiceLock = useRef(false),
    roundStartLevel = useRef<LevelState>(freshLevel()),
    recentContent = useRef<Record<string, string[]>>({}),
    syncedIds = useRef(new Set<string>());
  // Browser storage and auth are external sources restored once at startup.
  useEffect(() => {
    let active = true;
    const arrivedFromAuth =
      location.hash.includes("access_token=") ||
      location.hash.includes("error=") ||
      new URLSearchParams(location.search).has("code");
    const hash = new URLSearchParams(location.hash.slice(1));
    const query = new URLSearchParams(location.search);
    const callbackError =
      hash.get("error_description") || query.get("error_description");
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      setUser(data.session?.user ?? null);
      setAuthReady(true);
      if (arrivedFromAuth) {
        setMessage(
          callbackError ||
            error?.message ||
            (data.session
              ? "You’re signed in. Your progress will now sync securely."
              : "That sign-in link could not be completed. It may have expired or opened in a different browser. Request a fresh link here."),
        );
        history.replaceState({}, document.title, location.pathname);
      }
    });
    const { data } = supabase.auth.onAuthStateChange((event, s) => {
      if (!active) return;
      setUser(s?.user ?? null);
      setAuthReady(true);
      if (event === "PASSWORD_RECOVERY") {
        setRecovering(true);
        setAuth(true);
        return;
      }
      if (event === "SIGNED_IN") {
        setMessage("You’re signed in. Your progress will now sync securely.");
      }
    });
    if ("serviceWorker" in navigator)
      navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return () => {
      active = false;
      data.subscription.unsubscribe();
      if (recallTimer.current) clearTimeout(recallTimer.current);
    };
  }, []);
  useEffect(() => {
    if (!authReady) return;
    let active = true;
    syncedIds.current.clear();
    const owner = user?.id ?? null,
      target = owner ?? "guest";
    const localResults = safeParse<Result[]>(storageKey(owner, "results"), []),
      localLevels = normalizeLevels(
        safeParse<unknown>(storageKey(owner, "levels"), {}),
      ),
      localDeck = safeParse<DeckCard[]>(
        storageKey(owner, "deck"),
        initialDeck(),
      ),
      localResume = safeParse<number | null>(
        storageKey(owner, "workout"),
        null,
      );
    const restore = async () => {
      await Promise.resolve();
      if (!active) return;
      setResults(localResults);
      setLevels(localLevels);
      setDeck(mergeStarterDeck(localDeck.length ? localDeck : initialDeck()));
      setResumeIndex(
        typeof localResume === "number" &&
          localResume >= 0 &&
          localResume < DAILY_ORDER.length
          ? localResume
          : null,
      );
      if (!user) {
        setHydratedOwner(target);
        return;
      }
      const profileRequest = supabase
        .from("profiles")
        .select(
          "daily_goal_minutes,reminder_time,reminder_enabled,skill_levels,spaced_deck",
        )
        .eq("id", user.id)
        .maybeSingle();
      const remote: Result[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from("sessions")
          .select(
            "id,mode,score,correct,total,duration_seconds,played_at,level,effective_level,retention_check",
          )
          .order("played_at", { ascending: false })
          .range(from, from + 999);
        if (error || !data?.length) break;
        remote.push(...(data as Result[]));
        if (data.length < 1000) break;
      }
      const { data: profile } = await profileRequest;
      if (!active) return;
      remote.forEach((item) => syncedIds.current.add(item.id));
      if (profile) {
        setGoal(profile.daily_goal_minutes);
        setReminderEnabled(profile.reminder_enabled);
        setReminderTime(String(profile.reminder_time || "19:00").slice(0, 5));
        if (
          profile.skill_levels &&
          typeof profile.skill_levels === "object" &&
          Object.keys(profile.skill_levels).length
        )
          setLevels(normalizeLevels(profile.skill_levels));
        if (Array.isArray(profile.spaced_deck) && profile.spaced_deck.length)
          setDeck(mergeStarterDeck(profile.spaced_deck as DeckCard[]));
      }
      const merged = [
        ...remote,
        ...localResults.filter(
          (local) => !remote.some((item) => item.id === local.id),
        ),
      ].sort((a, b) => b.played_at.localeCompare(a.played_at));
      setResults(merged);
      setHydratedOwner(target);
    };
    void restore();
    return () => {
      active = false;
    };
  }, [authReady, user]);
  useEffect(() => {
    if (!auth || !user) return;
    let active = true;
    const detect = async () => {
      const support = pushSupport();
      if (support !== "ready") {
        if (active) setPushState(support);
        return;
      }
      if (Notification.permission === "denied") {
        if (active) setPushState("denied");
        return;
      }
      const subscription = await currentPushSubscription();
      if (active) setPushState(subscription ? "enabled" : "ready");
    };
    void detect();
    return () => {
      active = false;
    };
  }, [auth, user]);
  useEffect(() => {
    if (pushState !== "enabled" || !reminderEnabled) return;
    const timer = setTimeout(() => {
      void currentPushSubscription().then((subscription) => {
        if (subscription) void saveDevicePush(subscription, reminderTime);
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [pushState, reminderEnabled, reminderTime]);
  useEffect(() => {
    if (!authReady) return;
    const owner = user?.id ?? null;
    if (hydratedOwner !== (owner ?? "guest")) return;
    try {
      localStorage.setItem(
        storageKey(owner, "results"),
        JSON.stringify(results),
      );
      localStorage.setItem(storageKey(owner, "levels"), JSON.stringify(levels));
      localStorage.setItem(storageKey(owner, "deck"), JSON.stringify(deck));
      localStorage.setItem(
        storageKey(owner, "workout"),
        JSON.stringify(resumeIndex),
      );
    } catch {
      /* Keep the in-memory session if device storage is full. */
    }
  }, [authReady, user, hydratedOwner, results, levels, deck, resumeIndex]);
  useEffect(() => {
    if (!user || hydratedOwner !== user.id) return;
    const sync = () => {
      const pending = results.filter(
        (result) => !syncedIds.current.has(result.id),
      );
      if (!navigator.onLine || !pending.length) return;
      supabase
        .from("sessions")
        .upsert(
          pending.map((result) => ({ ...result, user_id: user.id })),
          { onConflict: "id", ignoreDuplicates: true },
        )
        .then(({ error }) => {
          if (error) console.warn("Offline sessions remain queued");
          else pending.forEach((result) => syncedIds.current.add(result.id));
        });
    };
    window.addEventListener("online", sync);
    if (navigator.onLine) sync();
    return () => window.removeEventListener("online", sync);
  }, [user, hydratedOwner, results]);
  const stats = useMemo(() => {
    const today = dayKey(new Date()),
      todaySeconds = results
        .filter((r) => dayKey(r.played_at) === today)
        .reduce((a, r) => a + r.duration_seconds, 0),
      activeDays = new Set(results.map((r) => dayKey(r.played_at)));
    let streak = 0,
      cursor = new Date();
    if (!activeDays.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
    while (activeDays.has(dayKey(cursor))) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    }
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const week = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return { done: activeDays.has(dayKey(d)), today: dayKey(d) === today };
    });
    return {
      todaySeconds,
      todayMinutes: Math.floor(todaySeconds / 60),
      totalMinutes: Math.floor(
        results.reduce((a, r) => a + r.duration_seconds, 0) / 60,
      ),
      accuracy: results.length
        ? Math.round(
            (results.reduce((a, r) => a + r.correct, 0) /
              Math.max(
                1,
                results.reduce((a, r) => a + r.total, 0),
              )) *
              100,
          )
        : 0,
      sessions: results.length,
      streak,
      week,
    };
  }, [results]);
  const practiceReady =
    authReady && hydratedOwner === (user?.id ?? "guest");

  function start(m: Mode) {
    if (!practiceReady) {
      setMessage("Your saved progress is still loading. Try again in a moment.");
      return;
    }
    if (recallTimer.current) clearTimeout(recallTimer.current);
    choiceLock.current = false;
    const thisRound = ++round.current,
      current = normalizeLevelState(levels[m.id]),
      check = current.level > 1 && (current.sessions + 1) % 5 === 0,
      effective = check ? current.level - 1 : current.level,
      difficulty = difficultyFor(m.id, effective),
      count = difficulty.itemCount;
    let nextSequence: string[];
    let spacedReview = false;
    roundStartLevel.current = current;
    setStory("");
    setChunkSize(0);
    setPeople([]);
    setRoundDeck([]);
    setStudyingNewCards(false);
    setMasteryGained(false);
    if (m.id === "numbers") {
      const recentNumbers = recentContent.current.numbers ?? [];
      nextSequence = createUniqueChunkNumber(
        difficulty.digitLength,
        recentNumbers,
      );
      const value = nextSequence.join("");
      recentContent.current.numbers = [
        value,
        ...recentNumbers.filter((item) => item !== value),
      ].slice(0, 12);
    } else if (m.id === "names") {
      const roundPeople = createPeopleRound({
        level: effective,
        count,
        recentNames: recentContent.current["names:names"],
        recentFeatures: recentContent.current["names:features"],
      });
      recentContent.current["names:names"] = roundPeople.nextRecentNames;
      recentContent.current["names:features"] =
        roundPeople.nextRecentFeatures;
      setPeople(roundPeople.people);
      nextSequence = roundPeople.people.map((person) => person.name);
    } else if (m.id === "spaced") {
      const due = orderDueCards(
        deck.filter((card) => new Date(card.dueAt) <= new Date()),
      );
      if (!due.length) {
        setGame(m);
        setRoundLevel(effective);
        setRetentionCheck(check);
        setLevelChange(0);
        setSequence([]);
        setChoices([]);
        setPhase("caughtup");
        return;
      }
      const reviews = due.filter((card) => card.intervalDays > 0);
      spacedReview = reviews.length > 0;
      const selected = (spacedReview
        ? reviews
        : due.filter((card) => card.intervalDays === 0)
      ).slice(0, difficulty.batchSize);
      setStudyingNewCards(!spacedReview);
      setRoundDeck(selected);
      nextSequence = selected.map((card) => card.answer);
    } else {
      const wordRound = createWordRound(
        effective,
        count,
        recentContent.current[m.id],
      );
      recentContent.current[m.id] = wordRound.nextRecent;
      nextSequence = wordRound.items;
    }
    setGame(m);
    setRoundLevel(effective);
    setRetentionCheck(check);
    setLevelChange(0);
    setSequence(nextSequence);
    setChoices(
      buildChoices(
        m,
        nextSequence,
        effective,
        deck.map((card) => card.answer),
      ),
    );
    pickedNow.current = [];
    setPicked([]);
    setPhase(m.id === "spaced" && spacedReview ? "recall" : "study");
    // start() is invoked by user actions, not during render; capture real practice time.
    // eslint-disable-next-line react-hooks/purity
    setStarted(Date.now());
    if (!["link", "numbers", "spaced"].includes(m.id))
      recallTimer.current = setTimeout(() => {
        if (round.current === thisRound) setPhase("recall");
      }, difficulty.studyMs);
  }
  function beginRecall(nextSequence = sequence) {
    if (!game) return;
    if (recallTimer.current) {
      clearTimeout(recallTimer.current);
      recallTimer.current = null;
    }
    setSequence(nextSequence);
    setChoices(
      buildChoices(
        game,
        nextSequence,
        roundLevel,
        deck.map((card) => card.answer),
      ),
    );
    pickedNow.current = [];
    setPicked([]);
    setPhase("recall");
  }
  function applyChunk(size: number) {
    const digits = sequence.join("");
    const chunks = digits.match(new RegExp(`.{1,${size}}`, "g")) || [digits];
    setChunkSize(size);
    beginRecall(chunks);
  }
  function undoPick() {
    const next = pickedNow.current.slice(0, -1);
    pickedNow.current = next;
    setPicked(next);
  }
  async function turnOnDevicePush() {
    setPushState("busy");
    const outcome = await enableDevicePush(reminderTime);
    if (outcome === "enabled") {
      setPushState("enabled");
    } else if (outcome === "denied") {
      setPushState("denied");
    } else {
      setPushState("ready");
      setMessage(
        "Could not turn on notifications. Check your connection and try again.",
      );
    }
  }
  function toggleReminder(enabled: boolean) {
    setReminderEnabled(enabled);
    if (!enabled && pushState === "enabled") {
      void disableDevicePush();
      setPushState("ready");
    }
  }
  function addDeckCard() {
    const prompt = newPrompt.trim(),
      answer = newAnswer.trim();
    if (prompt.length < 5 || answer.length < 1) return;
    if (
      deck.some(
        (card) =>
          card.prompt.toLowerCase() === prompt.toLowerCase() ||
          card.answer.toLowerCase() === answer.toLowerCase(),
      )
    ) {
      setMessage(
        "That question or answer is already in your deck. Answers must be unique so recall can be scored fairly.",
      );
      return;
    }
    setDeck((cards) => [
      ...cards,
      {
        id: crypto.randomUUID(),
        prompt,
        answer,
        intervalDays: 0,
        dueAt: new Date(0).toISOString(),
      },
    ]);
    setNewPrompt("");
    setNewAnswer("");
    setMessage("Card added. Save your profile to sync it across devices.");
  }
  function removeDeckCard(id: string) {
    setDeck((cards) => cards.filter((card) => card.id !== id));
    setMessage("Card removed. Save your profile to sync this change.");
  }
  function beginWorkout() {
    setWorkoutIndex(0);
    setResumeIndex(0);
    start(DAILY_ORDER[0]);
  }
  function resumeWorkout() {
    const index = resumeIndex ?? 0;
    setWorkoutIndex(index);
    start(DAILY_ORDER[index]);
  }
  function continueWorkout() {
    if (workoutIndex === null) return;
    const next = workoutIndex + 1;
    if (next < DAILY_ORDER.length) {
      setWorkoutIndex(next);
      setResumeIndex(next);
      start(DAILY_ORDER[next]);
    } else {
      setWorkoutIndex(null);
      setResumeIndex(null);
      setGame(null);
      setView("progress");
    }
  }
  function closeGame() {
    if (recallTimer.current) {
      clearTimeout(recallTimer.current);
      recallTimer.current = null;
    }
    round.current += 1;
    setWorkoutIndex(null);
    setGame(null);
  }
  function choose(w: string) {
    const current = pickedNow.current;
    if (phase !== "recall" || !game || choiceLock.current) return;
    const independentPrompts = ["names", "spaced"].includes(game.id);
    if (!independentPrompts && current.includes(w)) return;
    choiceLock.current = true;
    const next = [...current, w];
    pickedNow.current = next;
    setPicked(next);
    requestAnimationFrame(() => {
      choiceLock.current = false;
    });
    if (next.length === sequence.length) {
      setPhase("done");
      const completedAt = new Date();
      const correct = game.id === "numbers"
          ? next.reduce(
              (total, value, index) =>
                total + (value === sequence[index] ? value.length : 0),
              0,
            )
          : next.filter((x, i) => x === sequence[i]).length,
        total =
          game.id === "numbers"
            ? sequence.reduce((sum, value) => sum + value.length, 0)
            : sequence.length,
        ratio = correct / total;
      if (sound) tone(ratio >= 0.8);
      const previous = roundStartLevel.current;
      const result: Result = {
        id: crypto.randomUUID(),
        mode: game.id,
        score: correct * 120 * roundLevel,
        correct,
        total,
        duration_seconds: Math.max(
          1,
          Math.round((completedAt.getTime() - started) / 1000),
        ),
        played_at: completedAt.toISOString(),
        level: previous.level,
        effective_level: roundLevel,
        retention_check: retentionCheck,
      };
      setResults((currentResults) => [result, ...currentResults]);
      const progression = advanceLevel({
        state: previous,
        ratio,
        retentionCheck,
        attempts: total,
        minEvidence: difficultyFor(game.id, roundLevel).minEvidence,
      });
      const updated = {
        ...levels,
        [game.id]: progression.state,
      };
      setLevelChange(progression.levelChange);
      setMasteryGained(progression.masteryGained);
      setLevels(updated);
      let updatedDeck = deck;
      if (game.id === "spaced") {
        const now = completedAt.getTime();
        updatedDeck = deck.map((card) => {
          const index = roundDeck.findIndex((item) => item.id === card.id);
          if (index < 0) return card;
          const remembered = next[index] === sequence[index];
          const interval = remembered
            ? card.intervalDays === 0
              ? 1
              : card.intervalDays === 1
                ? 3
                : Math.min(30, Math.round(card.intervalDays * 2.2))
            : 0;
          return {
            ...card,
            intervalDays: interval,
            dueAt: new Date(
              now + (remembered ? interval : 1 / 24) * 86400000,
            ).toISOString(),
          };
        });
        setDeck(updatedDeck);
      }
      if (user) {
        supabase
          .from("profiles")
          .upsert({
            id: user.id,
            skill_levels: updated,
            spaced_deck: updatedDeck,
            updated_at: completedAt.toISOString(),
          })
          .then(({ error }) => {
            if (error) console.warn("Level sync deferred");
          });
      }
    }
  }
  async function signOut() {
    setMessage("");
    await supabase.auth.signOut();
    setUser(null);
    setAuth(false);
  }
  async function saveProfile() {
    if (!user) return;
    setMessage("Saving settings…");
    const { error } = await supabase.from("profiles").upsert({
      id: user.id,
      daily_goal_minutes: goal,
      reminder_enabled: reminderEnabled,
      reminder_time: reminderTime,
      skill_levels: levels,
      spaced_deck: deck,
      display_name: user.email?.split("@")[0],
      updated_at: new Date().toISOString(),
    });
    setMessage(
      error ? `Could not save: ${error.message}` : "Profile settings saved.",
    );
  }
  return (
    <main className="app-shell" data-build={APP_BUILD}>
      <header className="topbar">
        <button className="brand bare" onClick={() => setView("today")}>
          <span className="brand-mark">M</span>
          <span>MemSoup</span>
        </button>
        <div className="header-actions">
          <button
            className="sound bare"
            onClick={() => setSound(!sound)}
            aria-label="Toggle sound"
          >
            {sound ? "♪" : "×"}
          </button>
          <button
            className="account-button"
            onClick={() => {
              setMessage("");
              setAuth(true);
            }}
            aria-label={user ? "Open profile" : "Sign in"}
          >
            {user ? (
              <>
                <span className="avatar">
                  {user.email?.slice(0, 2).toUpperCase()}
                </span>
                <span>Profile</span>
              </>
            ) : (
              <>
                <span className="avatar">IN</span>
                <span>{authReady ? "Sign in" : "Checking…"}</span>
              </>
            )}
          </button>
        </div>
      </header>
      {view === "today" && (
        <>
          <section className="hero" id="top">
            <div>
              <p className="eyebrow">TODAY · YOUR PRACTICE</p>
              <h1>
                {user ? "Welcome back." : "Hello there."}
                <br />
                <span>Let’s make it stick.</span>
              </h1>
              <p className="hero-copy">
                Take a guided tour through all six techniques, with feedback and
                an optional redo at every stop.
              </p>
              <button
                className="primary"
                onClick={beginWorkout}
                disabled={!practiceReady}
              >
                {practiceReady ? "Start today’s workout" : "Loading progress…"}{" "}
                <span>→</span>
              </button>
              {resumeIndex !== null && (
                <button
                  className="resume-button"
                  onClick={resumeWorkout}
                  disabled={!practiceReady}
                >
                  Resume at {DAILY_ORDER[resumeIndex].title} · step{" "}
                  {resumeIndex + 1} of {DAILY_ORDER.length}
                </button>
              )}
            </div>
            <div className="daily-card">
              <div className="orb">
                <span>{stats.todayMinutes}</span>
                <small>OF {goal} MIN</small>
              </div>
              <div>
                <strong>Daily goal</strong>
                <p>
                  {stats.todaySeconds
                    ? `${stats.todayMinutes}m ${stats.todaySeconds % 60}s of actual practice today`
                    : "Ready when you are"}
                </p>
                <button className="mini-button" onClick={() => setAuth(true)}>
                  Goal settings
                </button>
              </div>
            </div>
          </section>
          <section className="streak-strip">
            <div>
              <span className="flame">◆</span>
              <strong>{stats.streak} day rhythm</strong>
              <small>
                {stats.streak
                  ? "Keep the chain useful, not stressful"
                  : "Practice today to begin"}
              </small>
            </div>
            <div className="week">
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                <span
                  key={d + i}
                  className={
                    stats.week[i].done
                      ? "done"
                      : stats.week[i].today
                        ? "today"
                        : ""
                  }
                >
                  {stats.week[i].done ? "✓" : d}
                </span>
              ))}
            </div>
          </section>
          <Modes
            start={start}
            showInfo={setInfo}
            levels={levels}
            ready={practiceReady}
          />
          <section className="insight-card">
            <span className="spark">✳</span>
            <div>
              <p className="eyebrow">TODAY’S MEMORY NOTE</p>
              <h3>Retrieval changes memory.</h3>
              <p>
                Trying to recall an answer can strengthen access more than
                simply reading it again. Difficulty can be useful; unsupported
                frustration is not.
              </p>
            </div>
          </section>
        </>
      )}
      {view === "train" && (
        <section className="page">
          <p className="eyebrow">SIX WAYS TO PRACTICE</p>
          <h1>Training kitchen</h1>
          <p className="hero-copy">
            Each mode teaches a transferable strategy, then lets you practice
            it. Scores measure this task—not intelligence.
          </p>
          <Modes
            start={start}
            showInfo={setInfo}
            levels={levels}
            ready={practiceReady}
          />
        </section>
      )}
      {view === "progress" && (
        <Analytics
          results={results}
          stats={stats}
          goal={goal}
          levels={levels}
          start={start}
          ready={practiceReady}
        />
      )}
      {view === "learn" && (
        <section className="page learn-page">
          <p className="eyebrow">THE WHY BEHIND THE PLAY</p>
          <h1>Memory field guide</h1>
          <p className="hero-copy">
            Memory improves when you encode with attention, build distinctive
            cues, retrieve without peeking, and revisit after time has passed.
            Use this guide to understand what each practice is teaching—not just
            how to score.
          </p>
          <div className="learn-principles">
            <article>
              <b>1</b>
              <div>
                <h3>Encode</h3>
                <p>
                  Turn information into an image, pattern, location, or meaning
                  you can retrieve later.
                </p>
              </div>
            </article>
            <article>
              <b>2</b>
              <div>
                <h3>Retrieve</h3>
                <p>
                  Hide the answer. A genuine attempt gives better feedback than
                  another passive reread.
                </p>
              </div>
            </article>
            <article>
              <b>3</b>
              <div>
                <h3>Space</h3>
                <p>
                  Return after some forgetting. Shorten the gap after failure
                  and lengthen it after easy success.
                </p>
              </div>
            </article>
          </div>
          <div className="learn-grid detailed">
            {MODES.map((m) => (
              <article key={m.id}>
                <span>{m.icon}</span>
                <h3>{m.title}</h3>
                <p>{m.science}</p>
                <h4>Use it</h4>
                <p>{m.how}</p>
                <div className="learn-example">
                  <strong>Example</strong>
                  {m.example}
                </div>
                <div className="learn-tip">
                  <strong>Mentor tip</strong>
                  {m.mentor}
                </div>
                <button
                  className="text-button"
                  onClick={() => start(m)}
                  disabled={!practiceReady}
                >
                  Practice this →
                </button>
              </article>
            ))}
          </div>
          <div className="truth">
            <strong>An honest promise</strong>
            <p>
              Practice reliably improves practiced skills and mnemonic use.
              Evidence for broad, lasting improvements to everyday cognition
              from commercial brain games is limited. MemSoup makes no medical,
              dementia-prevention, or IQ claims. Adaptive levels are a
              transparent practice heuristic, not a clinical assessment.
            </p>
          </div>
        </section>
      )}
      <nav className="bottom-nav">
        {(
          [
            ["today", "Today"],
            ["train", "Train"],
            ["progress", "Progress"],
            ["learn", "Learn"],
          ] as [View, string][]
        ).map(([v, l]) => (
          <button
            key={v}
            className={view === v ? "active" : ""}
            onClick={() => setView(v)}
          >
            {l}
          </button>
        ))}
      </nav>
      {game && (
        <div className="level-float">
          {workoutIndex !== null
            ? `Workout ${workoutIndex + 1} of ${DAILY_ORDER.length} · `
            : ""}
          Level {roundLevel}
          {retentionCheck ? " · level check" : ""}
          {phase === "done" && masteryGained
            ? " · mastery +1"
            : phase === "done" && levelChange !== 0
              ? levelChange > 0
                ? " · level up!"
                : " · level adjusted"
              : ""}
        </div>
      )}
      {game && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${game.title} practice`}
        >
          <div className="game-card">
            <button
              className="close"
              onClick={closeGame}
              aria-label="Close game"
            >
              ×
            </button>
            <p className="eyebrow">{game.title.toUpperCase()}</p>
            {phase === "caughtup" && (
              <div className="caught-up">
                <div className="celebrate">✓</div>
                <h2>You’re caught up.</h2>
                <p>
                  No cards are due yet. Reviewing them early would shorten the
                  spacing gap, so MemSoup will wait until the scheduled time.
                </p>
                <small>
                  Next review:{" "}
                  {new Date(
                    [...deck].sort((a, b) =>
                      a.dueAt.localeCompare(b.dueAt),
                    )[0]?.dueAt,
                  ).toLocaleString()}
                </small>
                {workoutIndex !== null ? (
                  <button className="primary" onClick={continueWorkout}>
                    Finish workout
                  </button>
                ) : (
                  <button className="primary" onClick={closeGame}>
                    Choose another technique
                  </button>
                )}
              </div>
            )}
            {phase === "study" && (
              <>
                <h2>
                  {game.id === "recall"
                    ? "Study with attention."
                    : game.id === "numbers"
                      ? "Choose useful chunks."
                    : game.id === "palace"
                      ? "Walk one familiar route."
                      : game.id === "spaced"
                          ? "Meet these new cards."
                          : "Build memorable cues."}
                </h2>
                <p>
                  {game.id === "spaced" && studyingNewCards
                    ? "Read each question and answer once. Future reviews will hide the answer and ask you to retrieve it first."
                    : game.how}
                </p>
                <div className="mini-example">
                  <strong>Example</strong>
                  <span>{game.example}</span>
                </div>
                <SequenceCards
                  game={game}
                  sequence={sequence}
                  people={people}
                  deck={roundDeck}
                />
                {game.id === "spaced" ? (
                  <div className="study-ready">
                    <small>
                      These are new. Once you begin, the answers stay hidden.
                    </small>
                    <button className="secondary" onClick={() => beginRecall()}>
                      Hide answers and begin
                    </button>
                  </div>
                ) : game.id === "link" ? (
                  <div className="strategy-builder">
                    <label htmlFor="link-story">Write your linking story</label>
                    <textarea
                      id="link-story"
                      value={story}
                      onChange={(event) => setStory(event.target.value)}
                      placeholder={`Example: ${sequence.slice(0, 3).join(" → ")}…`}
                    />
                    <button
                      className="primary"
                      disabled={
                        story.trim().length < 20 ||
                        storyCoverage(story, sequence) < sequence.length
                      }
                      onClick={() => beginRecall()}
                    >
                      Hide the list and recall
                    </button>
                    <small>
                      {storyCoverage(story, sequence)}/{sequence.length} list
                      words included. Use every word in one connected, vivid
                      story before recall.
                    </small>
                  </div>
                ) : game.id === "numbers" ? (
                  <div className="chunk-builder">
                    <strong>How will you group it?</strong>
                    <p>
                      Choose a chunk size. The same digits will then be tested
                      in your chosen groups.
                    </p>
                    <div>
                      {[2, 3, 4].map((size) => (
                        <button key={size} onClick={() => applyChunk(size)}>
                          Groups of {size}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="study-ready">
                    <small>
                      Recall begins automatically, or start once your cues feel
                      ready.
                    </small>
                    <button className="secondary" onClick={() => beginRecall()}>
                      I’m ready — start recall
                    </button>
                  </div>
                )}
              </>
            )}
            {phase === "recall" && (
              <>
                <h2>
                  {game.id === "names"
                    ? "Who matches this cue?"
                    : game.id === "spaced"
                      ? "Answer the due card."
                      : "What came next?"}
                </h2>
                <p>
                  {game.id === "palace"
                    ? `At ${PALACE_LOCI[picked.length] ?? "the next stop"}, what did you place there?`
                    : game.id === "names"
                      ? `${people[picked.length]?.avatar ?? ""} ${people[picked.length]?.feature ?? "Choose the matching name."}`
                      : game.id === "spaced"
                        ? roundDeck[picked.length]?.prompt
                        : game.id === "numbers"
                          ? `Rebuild the ${chunkSize}-digit chunks in order.`
                          : game.id === "link"
                            ? "Use the story you created, then tap the original order."
                            : "Retrieve the original order before choosing. Use Undo if a tap was accidental."}
                </p>
                <div className="picked">
                  {picked.map((w, i) => (
                    <span key={i}>
                      {i + 1}. {w}
                    </span>
                  ))}
                </div>
                {picked.length > 0 &&
                  !["names", "spaced"].includes(game.id) && (
                  <button className="undo-button" onClick={undoPick}>
                    Undo last choice
                  </button>
                  )}
                <div className="choices">
                  {choices.map((w) => (
                    <button
                      disabled={
                        !["names", "spaced"].includes(game.id) &&
                        picked.includes(w)
                      }
                      onClick={() => choose(w)}
                      key={w}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </>
            )}
            {phase === "done" && (
              <>
                <div className="celebrate">✦</div>
                <h2>
                  {results[0]?.correct === results[0]?.total
                    ? "Clean recall."
                    : "You made a memory trace."}
                </h2>
                <p>
                  {results[0]?.correct}/{results[0]?.total}{" "}
                  {["names", "spaced"].includes(game.id)
                    ? "correct"
                    : "in order"}{" "}
                  ·{" "}
                  {results[0]?.score} points
                </p>
                <button className="primary" onClick={() => start(game)}>
                  Redo this technique
                </button>
                {workoutIndex !== null ? (
                  <button className="secondary" onClick={continueWorkout}>
                    {workoutIndex === DAILY_ORDER.length - 1
                      ? "Finish workout"
                      : "Continue journey →"}
                  </button>
                ) : (
                  <button
                    className="secondary"
                    onClick={() => {
                      setGame(null);
                      setView("progress");
                    }}
                  >
                    See progress
                  </button>
                )}
                <Coach
                  mode={game}
                  result={results[0]}
                  sequence={sequence}
                  picked={picked}
                />
              </>
            )}
          </div>
        </div>
      )}
      {info && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label={`About ${info.title}`}
        >
          <div className="info-card">
            <button
              className="close"
              onClick={() => setInfo(null)}
              aria-label="Close technique information"
            >
              ×
            </button>
            <span className={`info-icon ${info.tone}`}>{info.icon}</span>
            <p className="eyebrow">TECHNIQUE GUIDE</p>
            <h2>{info.title}</h2>
            <p>{info.science}</p>
            <div className="info-block">
              <strong>How to use it</strong>
              <p>{info.how}</p>
            </div>
            <div className="info-block">
              <strong>Quick example</strong>
              <p>{info.example}</p>
            </div>
            <div className="mentor-tip">
              <strong>Mentor tip</strong>
              <p>{info.mentor}</p>
            </div>
            <button
              className="primary"
              disabled={!practiceReady}
              onClick={() => {
                setInfo(null);
                start(info);
              }}
            >
              Practice this technique
            </button>
          </div>
        </div>
      )}
      {auth && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label="MemSoup account"
        >
          <div className="auth-card">
            <button
              className="close"
              onClick={() => setAuth(false)}
              aria-label="Close account"
            >
              ×
            </button>
            <p className="eyebrow">YOUR MEMSOUP ACCOUNT</p>
            {user && !recovering ? (
              <>
                <h2>Profile settings</h2>
                <p className="signed-in-email">{user.email}</p>
                <p>
                  Your settings, levels, and completed sessions sync privately
                  when online. Games remain available offline.
                </p>
                <div className="settings">
                  <label htmlFor="daily-goal">
                    Daily practice goal
                    <span>Counted from actual completed-round time</span>
                  </label>
                  <select
                    id="daily-goal"
                    value={goal}
                    onChange={(e) => setGoal(+e.target.value)}
                  >
                    <option value="5">5 minutes</option>
                    <option value="10">10 minutes</option>
                    <option value="15">15 minutes</option>
                    <option value="20">20 minutes</option>
                    <option value="30">30 minutes</option>
                  </select>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={reminderEnabled}
                      onChange={(e) => toggleReminder(e.target.checked)}
                    />
                    <span>Daily practice reminder</span>
                  </label>
                  {reminderEnabled && (
                    <>
                      <label htmlFor="reminder-time">
                        Reminder time
                        <span>
                          Arrives as an app notification on each device where
                          you turn notifications on.
                        </span>
                      </label>
                      <input
                        id="reminder-time"
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                      />
                      {(pushState === "ready" || pushState === "busy") && (
                        <button
                          type="button"
                          className="secondary push-device"
                          disabled={pushState === "busy"}
                          onClick={() => void turnOnDevicePush()}
                        >
                          {pushState === "busy"
                            ? "Turning on…"
                            : "Turn on notifications on this device"}
                        </button>
                      )}
                      {pushState === "enabled" && (
                        <small className="push-note success">
                          Notifications are on for this device. MemSoup will
                          nudge you around {reminderTime} each day.
                        </small>
                      )}
                      {pushState === "needs-install" && (
                        <small className="push-note">
                          To get reminders on iPhone or iPad, first install
                          MemSoup: open memsoupapp.com in Safari, tap Share,
                          choose “Add to Home Screen,” then open the installed
                          app, sign in, and turn notifications on here.
                        </small>
                      )}
                      {pushState === "denied" && (
                        <small className="push-note">
                          Notifications are blocked for MemSoup on this device.
                          Allow them in the device settings, then return here.
                        </small>
                      )}
                      {pushState === "unsupported" && (
                        <small className="push-note">
                          This browser cannot deliver scheduled notifications.
                          The reminder time still syncs to your other devices.
                        </small>
                      )}
                    </>
                  )}
                  <div className="deck-manager">
                    <strong>Your Spaced Deck</strong>
                    <span>
                      {deck.length} cards · add a question you genuinely want to
                      remember
                    </span>
                    <label htmlFor="new-card-prompt">Question</label>
                    <input
                      id="new-card-prompt"
                      value={newPrompt}
                      onChange={(e) => setNewPrompt(e.target.value)}
                      placeholder="What is the main idea?"
                    />
                    <label htmlFor="new-card-answer">Answer</label>
                    <input
                      id="new-card-answer"
                      value={newAnswer}
                      onChange={(e) => setNewAnswer(e.target.value)}
                      placeholder="A short, specific answer"
                    />
                    <button
                      type="button"
                      className="secondary"
                      disabled={
                        newPrompt.trim().length < 5 || !newAnswer.trim()
                      }
                      onClick={addDeckCard}
                    >
                      Add card
                    </button>
                    {deck.filter(
                      (card) =>
                        !STARTER_DECK.some((starter) => starter.id === card.id),
                    ).length > 0 && (
                      <ul>
                        {deck
                          .filter(
                            (card) =>
                              !STARTER_DECK.some(
                                (starter) => starter.id === card.id,
                              ),
                          )
                          .map((card) => (
                            <li key={card.id}>
                              <span>
                                <b>{card.prompt}</b>
                                {card.answer}
                              </span>
                              <button
                                type="button"
                                aria-label={`Remove ${card.prompt}`}
                                onClick={() => removeDeckCard(card.id)}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                  <button className="primary" onClick={saveProfile}>
                    Save profile
                  </button>
                </div>
                {message && (
                  <p className="auth-message success" role="status">
                    {message}
                  </p>
                )}
                <button className="secondary sign-out" onClick={signOut}>
                  Sign out
                </button>
              </>
            ) : (
              <AuthForms
                recovering={recovering}
                finishRecovery={() => setRecovering(false)}
              />
            )}
          </div>
        </div>
      )}
    </main>
  );
}
function Modes({
  start,
  showInfo,
  levels,
  ready,
}: {
  start: (m: Mode) => void;
  showInfo: (m: Mode) => void;
  levels: Record<string, LevelState>;
  ready: boolean;
}) {
  return (
    <section className="section" id="modes">
      <div className="section-heading">
        <div>
          <p className="eyebrow">TRAIN WITH PURPOSE</p>
          <h2>Choose your practice</h2>
        </div>
      </div>
      <div className="mode-grid">
        {MODES.map((m, i) => (
          <article className={`mode-card ${m.tone}`} key={m.id}>
            <button
              className="mode-play"
              onClick={() => start(m)}
              disabled={!ready}
              aria-label={`Start ${m.title}`}
            >
              <span className="mode-icon">{m.icon}</span>
              <span className="mode-number">0{i + 1}</span>
              <strong>{m.title}</strong>
              <small>{m.note}</small>
              <span className="mode-meta">
                LEVEL {levels[m.id]?.level || 1} · {m.meta}
              </span>
            </button>
            <button
              className="info-button"
              onClick={() => showInfo(m)}
              aria-label={`Learn about ${m.title}`}
            >
              i
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
function Analytics({
  results,
  stats,
  goal,
  levels,
  start,
  ready,
}: {
  results: Result[];
  stats: {
    todaySeconds: number;
    todayMinutes: number;
    totalMinutes: number;
    accuracy: number;
    sessions: number;
    streak: number;
    week: { done: boolean; today: boolean }[];
  };
  goal: number;
  levels: Record<string, LevelState>;
  start: (m: Mode) => void;
  ready: boolean;
}) {
  const byMode = MODES.map((mode) => {
    const rows = results.filter((r) => r.mode === mode.id),
      recentRows = rows.slice(0, 12),
      correct = recentRows.reduce((a, r) => a + r.correct, 0),
      total = recentRows.reduce((a, r) => a + r.total, 0),
      accuracy = total ? Math.round((correct / total) * 100) : 0,
      sampleConfidence = Math.min(1, recentRows.length / 4),
      recencyGap = rows[0]
        ? Math.min(
            30,
            Math.max(
              0,
              results.findIndex((r) => r.id === rows[0].id),
            ),
          )
        : 30;
    return {
      mode,
      accuracy,
      sessions: rows.length,
      recentSessions: recentRows.length,
      level: levels[mode.id]?.level || 1,
      priority: rows.length
        ? (100 - accuracy) * sampleConfidence + recencyGap
        : 200,
    };
  });
  const focus = [...byMode].sort((a, b) => b.priority - a.priority)[0],
    recent = [...results].slice(0, 10).reverse();
  return (
    <section className="page analytics">
      <p className="eyebrow">YOUR IMPROVEMENT MAP</p>
      <h1>See what to do next.</h1>
      <div className="stat-grid">
        <Stat n={`${stats.todayMinutes}/${goal}`} label="minutes today" />
        <Stat n={`${stats.accuracy}%`} label="lifetime accuracy" />
        <Stat n={stats.streak} label="day rhythm" />
      </div>
      <div className="analytics-grid">
        <article className="viz-card goal-viz">
          <div>
            <p className="eyebrow">TODAY’S DOSAGE</p>
            <h3>
              {Math.max(0, goal * 60 - stats.todaySeconds) > 0
                ? `${Math.ceil(Math.max(0, goal * 60 - stats.todaySeconds) / 60)} min left`
                : "Goal complete"}
            </h3>
            <p>Only time inside completed practice rounds counts.</p>
          </div>
          <div
            className="goal-ring"
            style={
              {
                "--progress": `${Math.min(100, (stats.todaySeconds / (goal * 60)) * 100)}%`,
              } as React.CSSProperties
            }
          >
            <strong>
              {Math.min(
                100,
                Math.round((stats.todaySeconds / (goal * 60)) * 100),
              )}
              %
            </strong>
          </div>
        </article>
        <article className="viz-card">
          <p className="eyebrow">RECENT ACCURACY</p>
          <div
            className="spark-bars"
            aria-label="Accuracy over the last ten sessions"
          >
            {recent.map((r) => (
              <i
                key={r.id}
                role="img"
                aria-label={`${MODES.find((mode) => mode.id === r.mode)?.title ?? r.mode}: ${r.correct} of ${r.total}, level ${r.effective_level || 1}`}
                style={{
                  height: `${Math.max(8, (r.correct / Math.max(1, r.total)) * 100)}%`,
                }}
                title={`${r.correct}/${r.total}`}
              />
            ))}
          </div>
          <small>
            Oldest → newest. Look for a stable trend, not one perfect round.
          </small>
        </article>
      </div>
      <article className="viz-card skill-map">
        <div className="skill-heading">
          <div>
            <p className="eyebrow">TECHNIQUE MAP</p>
            <h3>Accuracy and adaptive level</h3>
          </div>
          <div className="legend">
            <span>accuracy</span>
            <b>level</b>
          </div>
        </div>
        {byMode.map((x) => (
          <div className="skill-row" key={x.mode.id}>
            <span>
              {x.mode.title}
              <small>
                {x.sessions} total · last {x.recentSessions} used
              </small>
            </span>
            <div>
              <i style={{ width: `${x.accuracy}%` }} />
            </div>
            <b>L{x.level}</b>
            <em>{x.sessions ? `${x.accuracy}%` : "new"}</em>
          </div>
        ))}
      </article>
      <article className="next-card">
        <div>
          <p className="eyebrow">BEST NEXT STEP</p>
          <h3>
            {results.length
              ? `Strengthen ${focus.mode.title}`
              : "Create your baseline"}
          </h3>
          <p>
            {results.length
              ? focus.sessions
                ? `${focus.accuracy}% across its last ${focus.recentSessions} round${focus.recentSessions === 1 ? "" : "s"}, balanced with recency and sample size. Practice one focused round, then use the specific mismatch feedback.`
                : `You have not established a ${focus.mode.title} baseline yet. One round will make future recommendations more balanced.`
              : "Complete one round in any mode. MemSoup will identify the most useful next practice from your own results."}
          </p>
        </div>
        <button
          className="primary"
          onClick={() => start(focus.mode)}
          disabled={!ready}
        >
          Train {focus.mode.title}
        </button>
      </article>
    </section>
  );
}
function Coach({
  mode,
  result,
  sequence,
  picked,
}: {
  mode: Mode;
  result: Result | undefined;
  sequence: string[];
  picked: string[];
}) {
  const ratio = (result?.correct || 0) / (result?.total || 1);
  const strong = ratio >= 0.8;
  const firstMiss = sequence.findIndex(
    (value, index) => picked[index] !== value,
  );
  const independentRecall = ["names", "spaced"].includes(mode.id);
  const example =
    mode.id === "numbers"
      ? `Try grouping ${sequence.slice(0, 4).join(" · ")} into two pairs with a steady rhythm.`
      : mode.id === "names"
        ? `Imagine “${sequence[0]}” interacting with one noticeable feature, then say the cue aloud.`
        : mode.id === "link"
          ? `Link ${sequence.slice(0, 3).join(", ")} with one moving, surprising action.`
          : mode.id === "spaced"
            ? "Say the full answer before tapping. Missed cards return sooner; remembered cards get a longer gap."
            : mode.id === "recall"
              ? `Cover the list and write what you remember before looking again.`
              : `Place ${sequence.slice(0, 3).join(", ")} at your door, sofa, and sink.`;
  return (
    <div className="coach">
      <strong>{strong ? "What worked" : "Coach’s note"}</strong>
      <p>
        {strong
          ? `Your cues held together well. ${mode.mentor}`
          : `Some cues faded. Slow down and make each connection more distinctive. ${mode.mentor}`}
      </p>
      <div className="feedback-example">
        <b>Try this:</b> {example}
      </div>
      {firstMiss >= 0 && (
        <div className="error-example">
          <b>{independentRecall ? "First missed item:" : "First mismatch:"}</b>{" "}
          {independentRecall ? "You chose" : `Position ${firstMiss + 1} was`} “
          {picked[firstMiss]}”; the target was “{sequence[firstMiss]}”.{" "}
          {independentRecall
            ? "Rebuild that item’s cue before the next review."
            : "Rebuild the cue at that transition first."}
        </div>
      )}
      <small>
        Next round:{" "}
        {strong
          ? "add one small challenge while keeping the same technique."
          : independentRecall
            ? "rebuild the first missed item, then retrieve it once without looking."
            : "focus on the first weak transition, rebuild that cue, then retrieve the full sequence once."}
      </small>
    </div>
  );
}
function Stat({ n, label }: { n: number | string; label: string }) {
  return (
    <div>
      <strong>{n}</strong>
      <span>{label}</span>
    </div>
  );
}
